// ═══════════════════════════════════════════════════════════════════════════
// RUN 14: Header Format Check
// ═══════════════════════════════════════════════════════════════════════════

import { safeAlert, reportResult, paraDetail, RunDetail, applydefaultparagraphformat } from '../runs/common';
import { getConfig, HeaderFormatConfig } from '../taskpane/configService';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (extracted to reduce cognitive complexity — S3776)
// ─────────────────────────────────────────────────────────────────────────────

/** Inject <w:titlePg/> into a section's OOXML if not already present. */
function injectTitlePg(ooxml: string): string | null {
  const docGridPos = ooxml.indexOf("<w:docGrid");
  if (docGridPos !== -1) {
    return ooxml.substring(0, docGridPos) + "<w:titlePg/>" + ooxml.substring(docGridPos);
  }

  const sectPrClose = ooxml.indexOf("</w:sectPr>");
  if (sectPrClose !== -1) {
    return ooxml.substring(0, sectPrClose) + "<w:titlePg/>" + ooxml.substring(sectPrClose);
  }

  return null; // nothing to patch
}

/** Determine which header types are active for a section. */
function getActiveHeaderTypes(
  hasTitlePg: boolean,
  evenAndOddEnabled: boolean
): { type: Word.HeaderFooterType; name: string }[] {
  const types: { type: Word.HeaderFooterType; name: string }[] = [
    { type: Word.HeaderFooterType.primary, name: "Primary" },
  ];
  if (hasTitlePg) types.push({ type: Word.HeaderFooterType.firstPage, name: "FirstPage" });
  if (evenAndOddEnabled) types.push({ type: Word.HeaderFooterType.evenPages, name: "EvenPages" });
  return types;
}

/** Fix bottom-border on a table (set or correct it). */
async function fixTableBottomBorder(
  table: Word.Table,
  cfg: HeaderFormatConfig,
  context: Word.RequestContext
): Promise<boolean> {
  const applyBorder = () => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const border = table.getBorder("Bottom");
    border.type = cfg.borderType as Word.BorderType;
    border.width = cfg.borderWidth;
    border.color = cfg.borderColor;
  };

  try {
    const bottomBorder = table.getBorder("Bottom");
    bottomBorder.load("type, width, color");
    await context.sync();

    const colorMatches =
      !bottomBorder.color ||
      bottomBorder.color.toLowerCase() === cfg.borderColor.toLowerCase() ||
      bottomBorder.color.toLowerCase() === "#000000";

    const needsFix =
      bottomBorder.type !== (cfg.borderType as Word.BorderType) ||
      bottomBorder.width !== cfg.borderWidth ||
      !colorMatches;

    if (needsFix) {
      applyBorder();
      await context.sync();
      return true;
    }
    return false;
  } catch (borderError) {
    // Border does not exist — create it
    console.log(`    Bottom border missing, adding: ${(borderError as Error).message}`);
    try {
      applyBorder();
      await context.sync();
      return true;
    } catch (error_) {
      console.log(`    ⚠ Could not set bottom border: ${(error_ as Error).message}`);
      return false;
    }
  }
}

/** Process all alignment fixes for every row/cell in a table. */
async function processTableAlignment(
  rows: Word.TableRowCollection,
  context: Word.RequestContext,
  nav: NavTracker
): Promise<number> {
  let fixed = 0;
  for (const row of rows.items) {
    const cells = row.cells;
    cells.load("items");
    await context.sync();

    for (let c = 0; c < cells.items.length; c++) {
      const cellParas = cells.items[c].body.paragraphs;
      cellParas.load("items");
      await context.sync();

      for (const para of cellParas.items) {
        para.load("alignment, text, uniqueLocalId");
        await context.sync();
        const changed = await fixParagraphAlignment(para, c, context, nav.add.bind(nav));
        if (changed) fixed++;
      }
    }
  }
  return fixed;
}

/** Process all bold + style fixes for every row/cell in a table. */
async function processTableBoldStyle(
  rows: Word.TableRowCollection,
  cfg: HeaderFormatConfig,
  context: Word.RequestContext,
  nav: NavTracker
): Promise<{ boldFixed: number; styleFixed: number }> {
  let boldFixed = 0;
  let styleFixed = 0;

  for (let r = 0; r < rows.items.length; r++) {
    const cells = rows.items[r].cells;
    cells.load("items");
    await context.sync();

    for (let c = 0; c < cells.items.length; c++) {
      const cellParas = cells.items[c].body.paragraphs;
      cellParas.load("items");
      await context.sync();

      for (let p = 0; p < cellParas.items.length; p++) {
        const para = cellParas.items[p];
        para.load("style, text, uniqueLocalId");
        para.font.load("bold");
        await context.sync();

        const [bFixed, sFixed] = await fixParagraphBoldAndStyle(
          para, cfg, r, c, p, context, nav.add.bind(nav)
        );
        if (bFixed) boldFixed++;
        if (sFixed) styleFixed++;
      }
    }
  }
  return { boldFixed, styleFixed };
}

/** Fix alignment for a single paragraph based on its cell column index. */
async function fixParagraphAlignment(
  para: Word.Paragraph,
  colIndex: number,
  context: Word.RequestContext,
  addNav: (uid: string, text: string, note: string) => void
): Promise<boolean> {
  let expectedAlignment: Word.Alignment | null;
  if (colIndex === 0) {
    expectedAlignment = Word.Alignment.left;
  } else if (colIndex === 1) {
    expectedAlignment = Word.Alignment.right;
  } else {
    expectedAlignment = null;
  }
  if (!expectedAlignment || para.alignment === expectedAlignment) return false;

  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  para.alignment = expectedAlignment;
  await context.sync();
  const alignLabel = colIndex === 0 ? "Left" : "Right";
  addNav(para.uniqueLocalId, (para.text ?? "").trim(), `→ Align ${alignLabel}`);
  console.log(`    Cell ${colIndex + 1}: Alignment → ${alignLabel} ✓`);
  return true;
}

/** Fix bold and style for a single paragraph. Returns [boldFixed, styleFixed]. */
async function fixParagraphBoldAndStyle(
  para: Word.Paragraph,
  cfg: HeaderFormatConfig,
  rowIdx: number,
  colIdx: number,
  paraIdx: number,
  context: Word.RequestContext,
  addNav: (uid: string, text: string, note: string) => void
): Promise<[boolean, boolean]> {
  const paraText = (para.text ?? "").trim();
  let boldFixed = false;
  let styleFixed = false;

  if (para.font.bold !== cfg.headerBold && paraText.length > 0) {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    para.font.bold = cfg.headerBold;
    await context.sync();
    boldFixed = true;
    addNav(para.uniqueLocalId, paraText, cfg.headerBold ? "→ Bold" : "→ Not Bold");
    console.log(`    Row ${rowIdx + 1} Cell ${colIdx + 1} P${paraIdx + 1}: Bold ✓`);
  }

  if ((para.style ?? "").toLowerCase() !== cfg.headerStyle.toLowerCase()) {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    para.style = cfg.headerStyle;
    await context.sync();
    styleFixed = true;
    addNav(para.uniqueLocalId, paraText, `→ ${cfg.headerStyle}`);
    console.log(`    Row ${rowIdx + 1} Cell ${colIdx + 1} P${paraIdx + 1}: Style → ${cfg.headerStyle} ✓`);
  }

  await applydefaultparagraphformat(para, cfg.headerStyle, context);
  return [boldFixed, styleFixed];
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-check processors
// ─────────────────────────────────────────────────────────────────────────────

async function processTitlePageSubCheck(
  sections: Word.SectionCollection,
  context: Word.RequestContext
): Promise<number> {
  let titlePageFixed = 0;
  console.log("── Sub-check 1: Title Page (Different First Page) ──");

  for (const section of sections.items) {
    const sectionOoxmlResult = section.body.getOoxml();
    await context.sync();
    const ooxml = sectionOoxmlResult.value;

    if (/<w:titlePg[\s/>]/.test(ooxml)) {
      console.log(`  Section: titlePg already present ✓`);
      continue;
    }

    const hasSectPr = /<w:sectPr\b[^>]*>/.test(ooxml);
    if (!hasSectPr) {
      console.log(`  Section: No sectPr found — skipped`);
      continue;
    }

    const modified = injectTitlePg(ooxml);
    if (!modified) {
      console.log(`  Section: No sectPr close tag — skipped`);
      continue;
    }

    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    section.body.insertOoxml(modified, "Replace");
    await context.sync();
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

    titlePageFixed++;
    console.log(`  Section: Added titlePg ✓`);
  }

  return titlePageFixed;
}

interface SectionFormatCounts {
  bordersFixed: number;
  alignmentFixed: number;
  boldFixed: number;
  styleFixed: number;
}

/** Bundles navigation-tracking state to reduce parameter count (S107). */
interface NavTracker {
  details: RunDetail[];
  seen: Set<string>;
  add(uid: string, text: string, note: string): void;
}

function makeNavTracker(details: RunDetail[], seen: Set<string>): NavTracker {
  return {
    details,
    seen,
    add(uid: string, text: string, note: string): void {
      if (!uid || this.seen.has(uid)) return;
      this.seen.add(uid);
      this.details.push(paraDetail(uid, text || "(header paragraph)", note));
    },
  };
}

/** Returns the loaded header body, or null if the section has no header of that type. */
async function tryGetHeaderBody(
  section: Word.Section,
  hType: Word.HeaderFooterType,
  context: Word.RequestContext
): Promise<Word.Body | null> {
  const headerBody = section.getHeader(hType);
  headerBody.load("tables, paragraphs");
  try {
    await context.sync();
    return headerBody;
  } catch (e) {
    // Word throws when the requested header type does not exist in this section.
    // This is an expected, recoverable condition — not a programming error.
    const reason = e instanceof Error ? e.message : String(e);
    console.log(`  Header type unavailable (${reason}) — skipped`);
    return null;
  }
}

async function processSectionHeader(
  section: Word.Section,
  sectionIdx: number,
  hType: Word.HeaderFooterType,
  typeName: string,
  cfg: HeaderFormatConfig,
  context: Word.RequestContext,
  nav: NavTracker
): Promise<SectionFormatCounts> {
  const counts: SectionFormatCounts = { bordersFixed: 0, alignmentFixed: 0, boldFixed: 0, styleFixed: 0 };

  const headerBody = await tryGetHeaderBody(section, hType, context);
  if (!headerBody) {
    console.log(`  Section ${sectionIdx + 1} ${typeName}: No header — skipped`);
    return counts;
  }

  const tables = headerBody.tables;
  tables.load("items");
  await context.sync();

  if (tables.items.length === 0) {
    console.log(`  Section ${sectionIdx + 1} ${typeName}: No table in header — skipped`);
    return counts;
  }

  console.log(`\n── Section ${sectionIdx + 1} ${typeName} Header (${tables.items.length} table(s)) ──`);

  for (const table of tables.items) {
    table.load("rowCount");
    await context.sync();
    console.log(`  Table: ${table.rowCount} row(s)`);

    // Sub-check 2: Bottom border
    const borderFixed = await fixTableBottomBorder(table, cfg, context);
    console.log(borderFixed ? `    Bottom border fixed ✓` : `    Bottom border OK ✓`);
    if (borderFixed) counts.bordersFixed++;

    // Load rows once for both passes
    const rows = table.rows;
    rows.load("items");
    await context.sync();

    // Sub-check 3: Alignment
    counts.alignmentFixed += await processTableAlignment(rows, context, nav);

    // Sub-checks 4 & 5: Bold + Style
    const { boldFixed, styleFixed } = await processTableBoldStyle(rows, cfg, context, nav);
    counts.boldFixed  += boldFixed;
    counts.styleFixed += styleFixed;
  }

  return counts;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export async function run18() {
  // S4325: cfg18 is already typed as HeaderFormatConfig via getConfig() — no cast needed
  const cfg18 = getConfig().run18_header;

  await Word.run(async (context) => {
    try {
      console.log("═".repeat(70));
      console.log("RUN 14: Header Format Check");
      console.log("═".repeat(70));

      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      console.log(`Total sections: ${sections.items.length}\n`);

      let titlePageFixed = 0;
      let bordersFixed   = 0;
      let alignmentFixed = 0;
      let boldFixed      = 0;
      let styleFixed     = 0;

      const navDetails: RunDetail[] = [];
      const navSeen = new Set<string>();

      // Sub-check 1: Title page
      titlePageFixed = await processTitlePageSubCheck(sections, context);

      // Re-load sections after potential OOXML changes
      const sectionsReload = context.document.sections;
      sectionsReload.load("items");
      await context.sync();

      // Detect document-level "different odd & even pages" setting
      let evenAndOddEnabled = false;
      try {
        const firstSectionOoxml = sectionsReload.items[0].body.getOoxml();
        await context.sync();
        evenAndOddEnabled = /<w:evenAndOddHeaders[\s/>]/.test(firstSectionOoxml.value);
      } catch (e) {
        console.log(`  Could not probe evenAndOddHeaders setting: ${(e as Error).message}`);
      }

      // Sub-checks 2–5 across all sections & header types
      for (let s = 0; s < sectionsReload.items.length; s++) {
        const section = sectionsReload.items[s];

        let hasTitlePg = false;
        try {
          const secOoxml = section.body.getOoxml();
          await context.sync();
          hasTitlePg = /<w:titlePg[\s/>]/.test(secOoxml.value);
        } catch (e) {
          console.log(`  Section ${s + 1}: Could not probe titlePg: ${(e as Error).message}`);
        }

        const activeHeaderTypes = getActiveHeaderTypes(hasTitlePg, evenAndOddEnabled);
        console.log(
          `  Section ${s + 1}: titlePg=${hasTitlePg} → processing ${activeHeaderTypes.map(h => h.name).join(", ")}`
        );

        for (const { type: hType, name: typeName } of activeHeaderTypes) {
          const nav = makeNavTracker(navDetails, navSeen);
          const counts = await processSectionHeader(
            section, s, hType, typeName, cfg18, context, nav
          );
          bordersFixed   += counts.bordersFixed;
          alignmentFixed += counts.alignmentFixed;
          boldFixed      += counts.boldFixed;
          styleFixed     += counts.styleFixed;
        }
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      const totalFixes = titlePageFixed + bordersFixed + alignmentFixed + boldFixed + styleFixed;

      console.log("\n" + "═".repeat(70));
      console.log(`Title Page added: ${titlePageFixed}`);
      console.log(`Borders fixed: ${bordersFixed}`);
      console.log(`Alignment fixed: ${alignmentFixed}`);
      console.log(`Bold applied: ${boldFixed}`);
      console.log(`Style fixed: ${styleFixed}`);
      console.log(`Total fixes: ${totalFixes}`);
      console.log("✓ Header Format check complete!");
      console.log("═".repeat(70));

      if (totalFixes > 0) {
        reportResult("Header Format Complete", [
          { label: "Title Page enabled (sections)", count: titlePageFixed },
          { label: "Borders fixed",                 count: bordersFixed   },
          { label: "Alignment fixed",               count: alignmentFixed },
          { label: "Bold applied",                  count: boldFixed      },
          { label: `Style → ${cfg18.headerStyle}`,  count: styleFixed     },
          ...navDetails,
        ]);
      } else {
        reportResult("Header Format — no changes needed", [
          { label: "All headers already correctly formatted", note: "No changes made" },
          { label: "Borders fixed",                           count: 0 },
          { label: "Alignment fixed",                         count: 0 },
          { label: "Bold applied",                            count: 0 },
          { label: `Style → ${cfg18.headerStyle}`,            count: 0 },
        ]);
      }
    } catch (error) {
      console.error("Error in run14:", error);
      console.error("Message:", (error as Error).message);
      safeAlert("An error occurred during Header Format check.\nCheck console (F12) for details.");
    }
  });
}